/**
 * Custom Multi-Variant Picker
 * Custom elements for multi-variant selection with per-variant quantity inputs.
 * Integrates with Dawn's pub/sub event system for media/price/SKU updates.
 */

class CustomVariant extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', this.onColorChange.bind(this));
    this.addEventListener('click', this.onDelegateClick.bind(this));
    this.addEventListener('change', this.onDelegateChange.bind(this));
    this.addEventListener('blur', this.onDelegateChange.bind(this));
    this.syncColorSelection();
  }

  /* ===== Color swatch click ===== */
  onColorChange(event) {
    const target = event.target;
    if (target.type !== 'radio') return;

    this.updateSelectedValueLabel();
    this.filterTableRows();
    this.recalcTotals();

    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const selectedInputs = this.querySelectorAll('.custom-color-swatch-input:checked');
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: Array.from(selectedInputs).map(
            (input) => input.dataset.optionValueId
          ),
        },
      });
    }
  }

  updateSelectedValueLabel() {
    const optionGroups = this.querySelectorAll('[data-option-group]');
    optionGroups.forEach((group) => {
      const label = group.querySelector('[data-option-group-selected-value-label]');
      if (!label) return;
      const checkedInput = group.querySelector('input[type="radio"]:checked');
      if (checkedInput) {
        label.textContent = checkedInput.value;
      }
    });
  }

  filterTableRows() {
    const selectedInput = this.querySelector('input[type="radio"]:checked');
    if (!selectedInput) return;

    const selectedColor = selectedInput.value;
    const table = this.querySelector('.custom-variant-table-wrapper');
    if (!table) return;

    const rows = table.querySelectorAll('.custom-variant-table-row');
    rows.forEach((row) => {
      if (row.dataset.option === selectedColor) {
        row.classList.add('show');
      } else {
        row.classList.remove('show');
      }
    });
  }

  syncColorSelection() {
    const selectedLabel = this.querySelector('[data-option-group-selected-value-label]');
    if (!selectedLabel) return;

    const initialValue = selectedLabel.textContent.trim();
    const inputs = this.querySelectorAll('.custom-color-swatch-input');
    inputs.forEach((input) => {
      input.checked = input.value === initialValue;
    });

    this.filterTableRows();
  }

  /* ===== Event delegation for quantity +/- buttons and inputs ===== */
  onDelegateClick(event) {
    const target = event.target;
    const wrapper = target.closest('.custom-quantity-widget');
    if (!wrapper) return;
    const input = wrapper.querySelector('input');

    if (target.closest('.custom-qty-plus')) {
      event.preventDefault();
      input.stepUp();
    } else if (target.closest('.custom-qty-minus')) {
      event.preventDefault();
      input.stepDown();
    } else {
      return;
    }

    this.syncTotals();
  }

  onDelegateChange(event) {
    const input = event.target;
    const wrapper = input.closest('.custom-quantity-widget');
    if (!wrapper) return;

    const currentValue = Number(input.value) || 0;
    const max = Number(input.max);
    if (currentValue > max && max > 0) input.value = max;
    else if (currentValue < 0) input.value = 0;

    this.syncTotals();
  }

  /* ===== Recalculate all totals ===== */
  syncTotals() {
    const sectionId = this.dataset.section;
    const table = document.querySelector(`#custom-variant-table-${sectionId}`);
    if (!table) return;

    const rows = table.querySelectorAll('.custom-variant-table-row');
    let totalQuantity = 0;
    let totalWeight = 0;
    let totalPrice = 0;
    let currentColorQty = 0;

    rows.forEach((row) => {
      const qtyEl = row.querySelector('.custom-quantity-widget');
      if (!qtyEl) return;
      const weight = Number(qtyEl.dataset.weight) || 0;
      const price = Number(qtyEl.dataset.price) || 0;
      const qty = Number(qtyEl.querySelector('input').value) || 0;
      totalQuantity += qty;
      totalWeight += weight * qty;
      totalPrice += price * qty;
      if (row.classList.contains('show')) {
        currentColorQty += qty;
      }
    });

    // Update totals display
    const totalEl = document.querySelector(`custom-total[data-section="${sectionId}"]`);
    if (totalEl) {
      totalEl.querySelector('.total-quantity').textContent = totalQuantity;
      totalEl.querySelector('.total-weight').textContent = totalWeight.toFixed(2);
      totalEl.querySelector('.total-price').textContent = (totalPrice / 100).toFixed(2);
    }

    this.updateBadge(currentColorQty);

    // Debounced external calls
    debouncedExternalCalls();

    // Sync mobile sticky bar
    const stickyQty = document.getElementById('mobile-sticky-qty');
    const stickyPrice = document.getElementById('mobile-sticky-price');
    if (stickyQty) stickyQty.textContent = totalQuantity;
    if (stickyPrice) stickyPrice.textContent = (totalPrice / 100).toFixed(2);
  }

  updateBadge(totalQty) {
    const selectedInput = document.querySelector('.custom-color-swatch-input:checked');
    if (!selectedInput) return;
    const colorItem = selectedInput.closest('.custom-color-swatch-item');
    if (!colorItem) return;
    const badge = colorItem.querySelector('.custom-v-badge');
    if (!badge) return;
    if (totalQty > 0) {
      badge.classList.remove('hidden');
      const countSpan = badge.querySelector('span');
      if (countSpan) countSpan.textContent = totalQty;
    } else {
      badge.classList.add('hidden');
    }
  }

  resetQuantities() {
    const inputs = this.querySelectorAll('.custom-quantity-widget input');
    inputs.forEach((input) => {
      input.value = '0';
    });
    this.syncTotals();
  }

  recalcTotals() {
    this.syncTotals();
  }
}

customElements.define('custom-variant', CustomVariant);

/**
 * Total summary display element
 */
class CustomTotal extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {}
}

customElements.define('custom-total', CustomTotal);

var debouncedExternalCalls = (function() {
  var timer;
  return function() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function() {
      if (typeof window.updateCartBtnState === 'function') window.updateCartBtnState();
      if (typeof window.updatePrintLogoFee === 'function') window.updatePrintLogoFee();
      if (typeof window.updateLogoSetupFee === 'function') window.updateLogoSetupFee();
    }, 150);
  };
})();
